// A FIFO queue that runs one async job at a time. Cloudflare Realtime's SFU
// is one RTCPeerConnection's worth of SDP state per session: only one
// offer/answer round trip can be in flight at once, or the second one lands
// while the first is still resolving and the two race (glare). Everything
// that touches an RTCPeerConnection's signaling state — publish, pull,
// renegotiate, unpublish — has to go through the SAME queue instance for
// the SAME session, so two calls that happen to overlap (e.g. a room
// broadcast for a second participant arriving while the first pull is still
// awaiting its renegotiate) run one after the other instead of concurrently.
//
// Pure and DOM-free on purpose (no RTCPeerConnection reference here) so it
// can be unit tested with plain `node --test` and reused wherever a serial
// negotiation queue is needed (a future recorder session, the app).

interface QueueItem {
  readonly run: () => Promise<unknown>;
  readonly resolve: (value: unknown) => void;
  readonly reject: (err: unknown) => void;
}

export class NegotiationQueue {
  private readonly items: QueueItem[] = [];
  private draining = false;

  /** Enqueues `run` and resolves once it (and everything queued before it)
   * has finished. Jobs never overlap: the next job only starts after the
   * previous one's promise has settled. */
  run<T>(run: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.items.push({
        run,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
      void this.drain();
    });
  }

  private async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      let item: QueueItem | undefined;
      while ((item = this.items.shift())) {
        try {
          item.resolve(await item.run());
        } catch (err) {
          item.reject(err);
        }
      }
    } finally {
      this.draining = false;
    }
  }
}
